import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomerModel } from "../models/CustomerModel";
import { OfferModel } from "../models/OfferModel";
import customerService from "../services/customerService";
import styles from './ViewCustomerPage.module.css';
import EditCustomerComp from "../components/EditCustomerComp";
import { formatDate } from "../utils/dateUtils";
import { formatPrice } from "../utils/currencyUtils";
import OfferTermsComp from "../components/OfferTermsComp";
import offerService from "../services/offerService";
import PaginationComp from "../components/PaginationComp";

const ViewCustomerPage = () => {
  const navigate = useNavigate();
  const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
  const location = useLocation();
  const initialCustomer = location.state?.customer ?? null;
  const [customer, setCustomer] = useState<CustomerModel | null>(initialCustomer);
  const [customerOffers, setCustomerOffers] = useState<OfferModel[]>([]);

  const [pageNum, setPageNum] = useState(0);
  const [totalCustomerOffers, setTotalCustomerOffers] = useState(0);
  const pageSize = 5;

  const [customerModalVisible, setCustomerModalVisible] = useState(false);

  const [serviceError, setServiceError] = useState('');

  useEffect(() => {
    if (customer) {

      fetchCustomerOffers(customer.sellerCustomerId);
    }
  }, [customer, pageNum]);

  const fetchCustomerOffers = async (customerId: number) => {
    try {
      const response = await customerService.getCustomerOffers(customerId, pageNum * pageSize, pageSize);
      setCustomerOffers(response.offers);
      setTotalCustomerOffers(response.offerCount);
    } catch (error) {
      setServiceError('Failed to load data. Please try again later.');
    }
  };

  const changePage = (newPageNum: number) => {
    setPageNum(newPageNum);
  };

  const updateTermsAccepted = async (offer: OfferModel) => {
    try {
      const result = await offerService.updateOfferTermsAccepted(offer.offerId);
      if (result === "Success") {
        setCustomerOffers(prevOffers => prevOffers.map(o => o.offerId === offer.offerId ? { ...o, termsAccepted: "Yes" } : o));
      }
    } catch (error: any) {
      setServiceError(error?.message || 'Failed to accept offer terms. Please try again later.');
    }
  }


  return (
    <div className="mt-3 mb-5">
      {(!customer || !customerOffers) && (<div className="text-center gap-2 mt-3 text-secondary">
        <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
        <span>  Loading...</span>
      </div>)}
      {customer && (
        <>
          <div className="card rounded-0">
            <div className="card-body p-4">
              <h5 className="card-title mb-4">{`${customer.firstname} ${customer.lastname}`}</h5>
              <div className="row mt-3">
                <div className="col-4">
                  <label className="text-label">Company</label>
                  <p className="text-value mb-2">{customer!.companyName}</p>
                </div>
                <div className="col-4">
                  <label className="text-label">Contact Number</label>
                  <p className="text-value mb-2">{customer.contactNumber}</p>
                </div>
              </div>
              <label className="text-label mt-3" >Notes</label>
              <p className="text-value mb-2 " style={{ whiteSpace: "pre-line" }}>{customer.notes || "-"}</p>
              <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setCustomerModalVisible(true)} >Edit</button>
            </div>
            <EditCustomerComp show={customerModalVisible} customer={customer}
              onClose={() => setCustomerModalVisible(false)}
              onCustomerUpdated={(updatedCustomer: CustomerModel) => {
                setCustomer(updatedCustomer);
                setCustomerModalVisible(false);
              }} />
          </div>
        </>
      )}
      {customerOffers && customerOffers.length > 0 && (
        <>
          <div className="card rounded-0 mt-3">
            <div className="card-body p-4">
              <h5 className="card-title mb-4">Offer History</h5>
              {customerOffers.map((offer, index) => (
                <div className={` pt-3 hover-bg-light ${index < customerOffers.length - 1 ? 'border-bottom' : 'pb-2'}`} style={{ cursor: 'pointer' }} key={offer.offerId} >
                  <div className={`row d-flex justify-content-space-between px-2`}>
                    <div className='col-md-6'>ID: #{offer.offerId}</div>
                    {offer.termsAccepted !== "" && <div className={`col-md-6 ${styles.offerCreatedItem} text-end`}>{formatDate(offer.createdTime)}</div>}
                  </div>

                  <div className={`align-items-center col-md-12 d-flex flex-row mb-4 mt-2 px-2`}>
                    {offer.termsAccepted === "" ?
                      (
                        <OfferTermsComp
                          offer={offer}
                          onAccept={updateTermsAccepted}
                        />

                      ) :
                      (
                        <div className={`d-flex flex-row flex-grow-1 text-muted small`} onClick={() => navigate(`/offers/view/${offer.offerId}`, { state: { offer: offer } })}>
                          <img className={`float-start border ${styles.offerItemImage}`} src={offer.itemImage ? `${itemImageURL}/${offer.itemImage}` : "/images/fallback.png"}></img>
                          <div className='flex-grow-1 ms-4'>
                            <p className='one-line-text-ellipsis mt-1 mb-2'>{offer.itemName}</p>
                            <div className='row'>
                              <div className={`col-md-2 float-start ${styles.offerPrice}`}>Price: {formatPrice(offer.itemPrice)}</div>
                              <div className={`col-md-3 float-start ${styles.offerQty}`}>Quantity: {offer.quantity}</div>
                              <div className={`col-md-7 text-end ${styles.offerAmount}`}>Amount: {formatPrice(offer.amount)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              ))}
              {serviceError && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}
            </div>
          </div>
          {/* Pagination */}
          <PaginationComp pageNum={pageNum} pageSize={pageSize} totalCount={totalCustomerOffers} onPageChange={changePage} />
        </>
      )}

    </div>

  );
}
export default ViewCustomerPage;